/* eslint-disable @typescript-eslint/no-unused-vars */
import { toast } from "sonner";
import { baseApi } from "../api/baseApi";
import {
  CreateWorkRequest,
  CrmCategory,
  CrmTeam,
  MyIncidents,
} from "../types/crm.types";
import { Work, WorkStateEnum } from "../types/work.types";

// Re-export types
export type {
  CreateWorkRequest,
  CrmCategory,
  CrmTeam,
  MyIncidents,
} from "../types/crm.types";

export const crmApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Ärendekategorier (träd). Statisk referensdata – cachas länge.
    getCrmCategories: builder.query<CrmCategory[], void>({
      query: () => "/v2/crm/categories",
      keepUnusedDataFor: 3600,
    }),
    // Team för ärenden. `Id` matchar kategoriernas DefaultTeamId.
    getCrmTeams: builder.query<CrmTeam[], void>({
      query: () => "/v2/crm/teams",
      providesTags: ["Teams"],
      keepUnusedDataFor: 3600,
    }),
    // Skapar ett nytt CRM-ärende (Work).
    createWork: builder.mutation<Work, CreateWorkRequest>({
      query: (body) => ({
        url: "/v2/crm/works",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Work", "Timeline", "MyCustomer", "Workitems"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Ärendet har skapats");
        } catch {
          toast.error("Det gick inte att skapa ärendet");
        }
      },
    }),
    postAddVisit: builder.mutation<void, { customerId: string }>({
      query: ({ customerId }) => ({
        url: `/v2/crm/customer/${customerId}/addvisit`,
        method: "POST",
      }),
      invalidatesTags: ["Visits"],
    }),
    getMyWorks: builder.query<MyIncidents, void>({
      query: () => {
        const url = "/v2/crm/works/my";
        return {
          url,
        };
      },
      providesTags: ["MyCustomer"],
    }),
    assignWork: builder.mutation<
      void,
      { workId: number; userId: number }
    >({
      query: ({ workId, userId }) => ({
        url: `/v2/crm/works/${workId}/assignTo/${userId}`,
        method: "POST",
      }),
      invalidatesTags: ["MyCustomer", "Work", "Timeline"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          toast.success("Ärendet har tilldelats");
        } catch {
          toast.error("Det gick inte att tilldela ärendet");
        }
      },
    }),
    setWorkState: builder.mutation<
      void,
      { workId: number; workState: WorkStateEnum }
    >({
      query: ({ workId, workState }) => ({
        url: `/v2/crm/works/${workId}/state`,
        method: "POST",
        body: { state: workState },
      }),
      invalidatesTags: ["MyCustomer", "Work", "Timeline"],
      async onQueryStarted({ workId, workState }, { queryFulfilled }) {
        try {
          await queryFulfilled;

          // Show success message based on state
          switch (workState) {
            case WorkStateEnum.Closed:
              toast.success("Ärendet har stängts");
              break;
            case WorkStateEnum.Solved:
              toast.success("Ärendet har markerats som löst");
              break;
            case WorkStateEnum.Open:
              toast.success("Ärendet har öppnats");
              break;
            default:
              toast.success("Ärendets status har uppdaterats");
          }
        } catch (error) {
          toast.error("Det gick inte att uppdatera ärendets status");
        }
      },
    }),
  }),
});

export const {
  useGetCrmCategoriesQuery,
  useGetCrmTeamsQuery,
  useCreateWorkMutation,
  useGetMyWorksQuery,
  usePostAddVisitMutation,
  useSetWorkStateMutation,
  useAssignWorkMutation,
} = crmApi;
